import { AdvancedMarker } from "@vis.gl/react-google-maps";
import { memo, useCallback, useEffect, useRef } from "react";
import { sportTypeIcons } from "@/lib/sport-icons";

const courtColors: Record<string, string> = {
  soccer_pitch: "#22c55e",
  tennis_court: "#eab308",
  padel_court: "#3b82f6",
  basketball_court: "#f97316",
  volleyball_court: "#a855f7",
  running_track: "#ef4444",
  baseball_diamond: "#92400e",
  rugby_pitch: "#65a30d",
  field_hockey_pitch: "#0d9488",
  cricket_pitch: "#d97706",
  skateboard_park: "#6366f1",
  multi_sport_court: "#64748b",
  beach_volleyball_court: "#f59e0b",
  petanque_court: "#78716c",
  street_workout_area: "#dc2626",
  pickleball_court: "#2563eb",
  ping_pong_table: "#059669",
  default: "#6b7280",
};

export function getCourtColor(sportType: string): string {
  return courtColors[sportType] ?? courtColors.default ?? "#6b7280";
}

interface SportCourtMarkerProps {
  courtId: string;
  position: google.maps.LatLngLiteral;
  sportType: string;
  isDraggable?: boolean;
  onDragEnd?: (lat: number, lng: number) => void;
  onClick?: (courtId: string) => void;
}

export const SportCourtMarker = memo(function SportCourtMarker({
  courtId,
  position,
  sportType,
  isDraggable,
  onDragEnd,
  onClick,
}: SportCourtMarkerProps) {
  const Icon = sportTypeIcons[sportType];
  const color = getCourtColor(sportType);
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);

  const handleClick = useCallback(() => {
    onClick?.(courtId);
  }, [onClick, courtId]);

  // Listen to dragend event on the underlying marker element
  useEffect(() => {
    const marker = markerRef.current;
    if (!marker || !isDraggable || !onDragEnd) return;

    const listener = marker.addListener("dragend", () => {
      const pos = marker.position;
      if (pos) {
        const lat = typeof pos.lat === "function" ? (pos as google.maps.LatLng).lat() : (pos as google.maps.LatLngLiteral).lat;
        const lng = typeof pos.lng === "function" ? (pos as google.maps.LatLng).lng() : (pos as google.maps.LatLngLiteral).lng;
        onDragEnd(lat, lng);
      }
    });

    return () => {
      listener.remove();
    };
  }, [isDraggable, onDragEnd]);

  return (
    <AdvancedMarker
      ref={(marker) => { markerRef.current = marker; }}
      position={position}
      onClick={handleClick}
      zIndex={1100}
      draggable={isDraggable}
    >
      <div
        className="flex items-center justify-center rounded-full border-2 border-white shadow-md cursor-pointer transition-transform hover:scale-110"
        style={{
          width: 24,
          height: 24,
          backgroundColor: color,
        }}
      >
        {Icon && <Icon size={14} stroke={1.5} color="white" />}
      </div>
    </AdvancedMarker>
  );
});
